import { useScopedSelector } from 'ia-react-core';
import React, { FC, useMemo } from 'react';
import { Form, isNullOrWhitespaces, FormSection } from 'omni-package-react-core';
import { useTranslation } from 'react-i18next';
import ObjectRow from './FormRow/ObjectRow';
import SubjectRow from './FormRow/SubjectRow';
import SpecifyRow from './FormRow/SpecifyRow';
import { StyledBlock } from '../NewMessagePage.styles';
import getApiRoot from '../../../utils/getApiRoot';
import { NewMessagePageState } from '../../../interfaces/NewMessagePageState';
import MessageRow from '../../../components/_MessageRow';
import AttachmentRow from '../../../components/_AttachmentsRow';
import { Message } from '../../../interfaces/Message';
import { MessageDto } from '../../../interfaces/dtos/MessageDto';
import { Mail } from '../../../interfaces/Mail';
import ContractNumberRow from './FormRow/ContractNumberRow';
import AssignmentsLoader from './FormRow/AssignmentsRow/Loader/AssignmentsLoader';
import AssignmentsRow from './FormRow/AssignmentsRow/AssignmentsRow';
import { Assignments } from '../../../interfaces/Assignments';
import Constants from '../../../Constants';
import ContractRow from './FormRow/ContractRow';

export interface NewMessageFormProps {
  messageSource: string;
}

const NewMessagePage: FC<NewMessageFormProps> = ({ messageSource }) => {
  const { t } = useTranslation('NewMessagePage');
  const isClient: boolean = useScopedSelector((state: NewMessagePageState) => state.isClient);
  const assignments: Assignments[] = useScopedSelector((state: NewMessagePageState) => state.subjectAssignments);
  const mail = useScopedSelector((state: NewMessagePageState) => state.mailFormValues);
  const isOtherSubject = mail?.subject?.freeTextAllowed;
  const excludedContractMessage = useMemo(
    () => isClient ? mail?.contract?.systemExclusionMessage :
      mail?.businessLine?.excludedFromSystemMessage,
    [isClient, mail?.businessLine?.excludedFromSystemMessage, mail?.contract?.systemExclusionMessage],
  );
  const isExcludedContract = !isNullOrWhitespaces(excludedContractMessage);

  const buildPayload = (values: Mail) => (
    {
      content: values?.message,
      attachments: values?.attachments,
      contractNumber: values?.contract?.number,
      businessLineCode: values?.businessLine?.code,
      contractText: values?.contractText,
      subjectId: values?.subject?.id,
      freeTextSubject: values?.specify,
      subjectAssignmentId: !isNullOrWhitespaces(values?.subjectAssignment) ? values?.subjectAssignment : null,
    });

  return (
    <Form<Message, { data: MessageDto }>
      name="mailForm"
      url={`${getApiRoot()}/messages`}
      submitButtonText={t('sendMessageButton')}
      transformRequest={(values: Mail) => buildPayload(values)}
      submitDisabled={isExcludedContract}
      controlErrorMapping={{
        ContractNumber: 'contract',
        Content: 'message',
        SubjectId: 'subject',
        BusinessLineCode: 'businessLine',
        FreeTextSubject: 'specify',
        SubjectAssignmentId: 'subjectAssignment',
        ContractText: 'contractText',
        Attachments: 'attachments',
      }}
      confirmationPath={(rep: { data: MessageDto }) => `${Constants.NewMessageConfirmationPagePath}/${encodeURIComponent(rep?.data?.referenceNumber)}?messageSource=${messageSource}`}
    >
      <FormSection>
        <ContractRow isClient={isClient} excludedContractMessage={excludedContractMessage} />
        <ObjectRow />
        <SubjectRow />
        <AssignmentsLoader mailSubjectId={mail?.subject?.id} />
        {(isOtherSubject || (assignments?.length > 0)) && (
          <StyledBlock nested p-b={12}>
            {isOtherSubject && <SpecifyRow />}
            {(assignments?.length > 0) && <AssignmentsRow assignments={assignments} />}
          </StyledBlock>
        )}

        {!isClient && (<ContractNumberRow disabled={!mail?.businessLine || isExcludedContract} />)}
        <AttachmentRow disabled={isExcludedContract} currentAttachmentNumber={mail?.attachments?.length} />
        <MessageRow disabled={isExcludedContract} />
      </FormSection>
    </Form>
  );
};

export default NewMessagePage;
